/**
 * What billing contributes, in two groups mounted separately.
 *
 * `billingRoutes` is the back office's: the ledger, one document, and one
 * customer's situation. All three read `billing.document.read`, and what a
 * user can *do* on them is decided by the two write permissions, which the
 * screens read themselves. That split is the one that matters at a
 * distributor: issuing an invoice and declaring one paid are different jobs,
 * often different people.
 *
 * `portalBillingRoutes` is the customer's. No permission on either, and that
 * is the design rather than an omission: a customer's rights are the state of
 * their `client_users` link, which the server resolves on every request.
 *
 * The portal's routes take `clientCode` as a **function**, called during
 * render. The customer context lives in `feature-shop` and a feature may not
 * import a sibling feature, so the composition root — which is allowed to
 * know both exist — passes a reader in. Being called during render is what
 * lets that reader be a hook.
 */

import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { DocumentScreen } from "./screens/DocumentScreen";
import { LedgerScreen } from "./screens/LedgerScreen";
import { PortalDocumentsScreen } from "./screens/PortalDocumentsScreen";
import { PortalStatementScreen } from "./screens/PortalStatementScreen";
import { StatementScreen } from "./screens/StatementScreen";
import { BILLING_PATHS, BILLING_PORTAL_PATHS } from "./paths";
import "./i18n";

export function billingRoutes(client: ApiClient): FeatureRoutes {
  function Ledger() {
    return <LedgerScreen client={client} />;
  }

  function OneDocument() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types. The id is
    // validated by the server on every call it is used in.
    const params: { id?: string } = useParams({ strict: false });
    return <DocumentScreen client={client} id={params.id ?? ""} />;
  }

  function OneStatement() {
    const params: { id?: string } = useParams({ strict: false });
    return <StatementScreen client={client} id={params.id ?? ""} />;
  }

  return {
    id: "billing",
    routes: [
      {
        path: BILLING_PATHS.ledger,
        component: Ledger,
        layout: "app",
        requirement: { authenticated: true, permissions: ["billing.document.read"] },
        nav: {
          group: "billing:group",
          labelKey: "billing:nav.ledger",
          section: "main",
          order: 45,
        },
      },
      {
        // Declared BEFORE `/facturation/$id`: `/facturation/clients/…` would
        // otherwise be read as a document whose id is "clients", and the
        // screen would report "document introuvable".
        path: BILLING_PATHS.statement,
        component: OneStatement,
        layout: "app",
        requirement: { authenticated: true, permissions: ["billing.document.read"] },
      },
      {
        path: BILLING_PATHS.document,
        component: OneDocument,
        layout: "app",
        requirement: { authenticated: true, permissions: ["billing.document.read"] },
      },
    ],
  };
}

export interface PortalBillingOptions {
  /**
   * The company being acted for.
   *
   * Read during render, so it may be a hook. Returning an empty string is
   * valid and means "whichever company the session resolves to" — the server
   * decides, and it refuses a code the account is not linked to.
   */
  clientCode: () => string;
  /** For the page's eyebrow. Optional: the code alone is enough to be honest. */
  companyName?: () => string;
}

export function portalBillingRoutes(
  client: ApiClient,
  options: PortalBillingOptions,
): FeatureRoutes {
  function Documents() {
    const companyName = options.companyName?.();
    return (
      <PortalDocumentsScreen
        client={client}
        clientCode={options.clientCode()}
        {...(companyName ? { companyName } : {})}
      />
    );
  }

  function AccountStatement() {
    const companyName = options.companyName?.();
    return (
      <PortalStatementScreen
        client={client}
        clientCode={options.clientCode()}
        {...(companyName ? { companyName } : {})}
      />
    );
  }

  return {
    id: "billing-portal",
    routes: [
      {
        path: BILLING_PORTAL_PATHS.documents,
        component: Documents,
        layout: "app",
        requirement: { authenticated: true },
        nav: { labelKey: "billing:nav.documents", section: "main", order: 14 },
      },
      {
        path: BILLING_PORTAL_PATHS.statement,
        component: AccountStatement,
        layout: "app",
        requirement: { authenticated: true },
        nav: { labelKey: "billing:nav.account", section: "main", order: 15 },
      },
    ],
  };
}
